import React, { useState } from 'react';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';
import { User } from 'firebase/auth';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';

interface RetirementNpsCalculatorProps {
  currentUser: User | null;
}

const RetirementNpsCalculator: React.FC<RetirementNpsCalculatorProps> = ({ currentUser }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState<number>(5000);
  const [currentAge, setCurrentAge] = useState<number>(25);
  const [expectedReturn, setExpectedReturn] = useState<number>(10);
  const [retirementAge] = useState<number>(60);
  const [result, setResult] = useState<any>(null);

  const calculateNps = () => {
    const investmentPeriod = retirementAge - currentAge;
    const totalMonths = investmentPeriod * 12;
    const rateOfReturn = expectedReturn / 100;

    const totalInvestment = monthlyInvestment * totalMonths;
    const estimatedReturns = Math.round(totalInvestment * (Math.pow(1 + rateOfReturn / 1, investmentPeriod) - 1));
    const totalCorpus = totalInvestment + estimatedReturns;
    
    // Assuming 40% of corpus is used for annuity with 6% return
    const annuityInvestment = totalCorpus * 0.4;
    const lumpSum = totalCorpus * 0.6;
    const monthlyPension = (annuityInvestment * (0.06)) / 12;


    setResult({
      totalInvestment: totalInvestment.toFixed(2),
      totalCorpus: totalCorpus.toFixed(2),
      estimatedReturns: estimatedReturns.toFixed(2),
      monthlyPension: monthlyPension.toFixed(2),
      lumpSum: lumpSum.toFixed(2),
    });
  };

  return (
    <Card className="p-4 shadow-sm">
      <h4 className="mb-4 text-center">NPS/Retirement Calculator</h4>
      <Form>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={5}>Monthly Investment (₹)</Form.Label>
          <Col sm={7}>
            <Form.Control
              type="number"
              value={monthlyInvestment}
              onChange={(e) => setMonthlyInvestment(parseFloat(e.target.value))}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={5}>Your Current Age</Form.Label>
          <Col sm={7}>
            <Form.Control
              type="number"
              value={currentAge}
              onChange={(e) => setCurrentAge(parseFloat(e.target.value))}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={5}>Expected Return Rate (%)</Form.Label>
          <Col sm={7}>
            <Form.Control
              type="number"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(parseFloat(e.target.value))}
            />
          </Col>
        </Form.Group>

        <div className="text-center">
            <Button variant="primary" onClick={calculateNps}>
            Calculate
            </Button>
            {currentUser && result && (
              <Button variant="secondary" onClick={async () => {
                try {
                  await addDoc(collection(db, `users/${currentUser.uid}/savedCalculations`), {
                    calculatorType: 'nps',
                    monthlyInvestment,
                    currentAge,
                    expectedReturn,
                    result,
                    timestamp: new Date(),
                  });
                  alert('NPS calculation saved!');
                } catch (e) {
                  console.error('Error saving document: ', e);
                  alert('Error saving calculation.');
                }
              }} className="ms-2">
                Save Calculation
              </Button>
            )}
        </div>

        {result && (
          <div className="mt-4">
            <h5 className="text-center">Retirement Projections</h5>
            <Row>
              <Col><strong>Total Investment:</strong></Col>
              <Col className="text-end">₹{result.totalInvestment}</Col>
            </Row>
            <Row>
              <Col><strong>Estimated Returns:</strong></Col>
              <Col className="text-end">₹{result.estimatedReturns}</Col>
            </Row>
            <Row>
              <Col><strong>Total Corpus:</strong></Col>
              <Col className="text-end">₹{result.totalCorpus}</Col>
            </Row>
            <hr />
            <p className="text-center text-muted">Assuming 40% of corpus for annuity at 6% return:</p>
            <Row>
              <Col><strong>Lump Sum Withdrawal:</strong></Col>
              <Col className="text-end">₹{result.lumpSum}</Col>
            </Row>
            <Row>
              <Col><strong>Monthly Pension:</strong></Col>
              <Col className="text-end">₹{result.monthlyPension}</Col>
            </Row>
          </div>
        )}
      </Form>
    </Card>
  );
};

export default RetirementNpsCalculator;
